import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaPhoneAlt,
  FaSearch,
  FaSyncAlt,
  FaSave,
  FaUserGraduate,
  FaWhatsapp,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function getStaffId() {
  const staff = SessionManager.getStaffDetails();
  return staff?.staff_id || "";
}

function normalizeContact(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") {
    return "";
  }
  return s.replace(/[^\d+]/g, "").slice(0, 15);
}

function normalizeWhatsApp(value) {
  return value === true || value === 1 || value === "1" ? 1 : 0;
}

function normalizeContactsFromRow(item) {
  let contact1 = normalizeContact(item.contact1);
  let contact2 = normalizeContact(item.contact2);
  let contact_id1 = item.contact_id1 != null ? String(item.contact_id1) : "";
  let contact_id2 = item.contact_id2 != null ? String(item.contact_id2) : "";
  let contact_person_name1 = item.contact_person_name1 || "";
  let contact_person_name2 = item.contact_person_name2 || "";
  let contact_person_relation1 = item.contact_person_relation1 || "";
  let contact_person_relation2 = item.contact_person_relation2 || "";
  let is_whatsapp1 = normalizeWhatsApp(item.is_whatsapp1);
  let is_whatsapp2 = normalizeWhatsApp(item.is_whatsapp2);

  if (
    (!contact1 && !contact2) &&
    Array.isArray(item.contact_details) &&
    item.contact_details.length > 0
  ) {
    const c1 = item.contact_details[0];
    const c2 = item.contact_details[1];
    if (c1) {
      contact1 = normalizeContact(c1.contact_number);
      contact_id1 = c1.id != null ? String(c1.id) : "";
      contact_person_name1 = c1.contact_person_name || "";
      contact_person_relation1 = c1.contact_person_relation || "";
      is_whatsapp1 = normalizeWhatsApp(c1.is_whatsapp_number);
    }
    if (c2) {
      contact2 = normalizeContact(c2.contact_number);
      contact_id2 = c2.id != null ? String(c2.id) : "";
      contact_person_name2 = c2.contact_person_name || "";
      contact_person_relation2 = c2.contact_person_relation || "";
      is_whatsapp2 = normalizeWhatsApp(c2.is_whatsapp_number);
    }
  }

  return {
    contact1,
    contact2,
    contact_id1,
    contact_id2,
    contact_person_name1,
    contact_person_name2,
    contact_person_relation1,
    contact_person_relation2,
    is_whatsapp1,
    is_whatsapp2,
  };
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const StudentContactRow = memo(function StudentContactRow({
  student,
  draft,
  index,
  onChange,
}) {
  if (!draft) return null;

  const classLabel =
    student.section_id === "No"
      ? student.class_name
      : `${student.class_name || ""}:${student.section_name || ""}`;

  const base = normalizeContactsFromRow(student);
  const c1Dirty =
    normalizeContact(draft.contact1) !== normalizeContact(base.contact1) ||
    normalizeWhatsApp(draft.is_whatsapp1) !==
      normalizeWhatsApp(base.is_whatsapp1);
  const c2Dirty =
    normalizeContact(draft.contact2) !== normalizeContact(base.contact2) ||
    normalizeWhatsApp(draft.is_whatsapp2) !==
      normalizeWhatsApp(base.is_whatsapp2);

  return (
    <div
      className={`rn-row rn-row--contacts${
        c1Dirty || c2Dirty ? " rn-row--dirty" : ""
      }`}
      style={{ "--rn-i": index }}
    >
      <div className="rn-row__who">
        <StudentAvatar
          name={student.stu_name}
          imagePath={student.student_img_path}
        />
        <div className="min-w-0">
          <div className="rn-row__name">{student.stu_name || "—"}</div>
          <div className="rn-row__chips">
            <span className="rn-chip rn-chip--father">
              {student.father_name ? `S/O ${student.father_name}` : "—"}
            </span>
            {classLabel ? (
              <span className="rn-chip rn-chip--class">{classLabel}</span>
            ) : null}
            {student.student_roll_no ? (
              <span className="rn-chip rn-chip--enroll">
                Roll: {student.student_roll_no}
              </span>
            ) : null}
            <span className="rn-chip rn-chip--enroll">
              ID: {student.student_enrollment_no || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className={`rn-row__field${c1Dirty ? " rn-row__field--dirty" : ""}`}>
        <label className="rn-row__label" htmlFor={`contact1-${draft.id}`}>
          Contact 1
        </label>
        <div className="rn-input">
          <span
            className="rn-input__prefix rn-input__prefix--contact1"
            aria-hidden
          >
            <FaPhoneAlt />
          </span>
          <input
            id={`contact1-${draft.id}`}
            type="tel"
            inputMode="tel"
            className="rn-input__control"
            value={draft.contact1 ?? ""}
            onChange={(e) =>
              onChange(draft.id, "contact1", normalizeContact(e.target.value))
            }
            placeholder="Mobile number"
            autoComplete="off"
          />
        </div>
        <label className="rn-wa-toggle" htmlFor={`wa1-${draft.id}`}>
          <input
            id={`wa1-${draft.id}`}
            type="checkbox"
            checked={normalizeWhatsApp(draft.is_whatsapp1) === 1}
            onChange={(e) =>
              onChange(draft.id, "is_whatsapp1", e.target.checked ? 1 : 0)
            }
            disabled={!normalizeContact(draft.contact1)}
          />
          <FaWhatsapp className="rn-wa-toggle__icon" aria-hidden />
          <span>WhatsApp</span>
        </label>
        <div className="rn-row__hint">
          {base.contact_person_relation1 || base.contact_person_name1
            ? `${base.contact_person_relation1 || "—"}${
                base.contact_person_name1 ? ` · ${base.contact_person_name1}` : ""
              }`
            : `Current: ${base.contact1 || "Not set"}`}
        </div>
      </div>

      <div className={`rn-row__field${c2Dirty ? " rn-row__field--dirty" : ""}`}>
        <label className="rn-row__label" htmlFor={`contact2-${draft.id}`}>
          Contact 2
        </label>
        <div className="rn-input">
          <span
            className="rn-input__prefix rn-input__prefix--contact2"
            aria-hidden
          >
            <FaPhoneAlt />
          </span>
          <input
            id={`contact2-${draft.id}`}
            type="tel"
            inputMode="tel"
            className="rn-input__control"
            value={draft.contact2 ?? ""}
            onChange={(e) =>
              onChange(draft.id, "contact2", normalizeContact(e.target.value))
            }
            placeholder="Mobile number"
            autoComplete="off"
          />
        </div>
        <label className="rn-wa-toggle" htmlFor={`wa2-${draft.id}`}>
          <input
            id={`wa2-${draft.id}`}
            type="checkbox"
            checked={normalizeWhatsApp(draft.is_whatsapp2) === 1}
            onChange={(e) =>
              onChange(draft.id, "is_whatsapp2", e.target.checked ? 1 : 0)
            }
            disabled={!normalizeContact(draft.contact2)}
          />
          <FaWhatsapp className="rn-wa-toggle__icon" aria-hidden />
          <span>WhatsApp</span>
        </label>
        <div className="rn-row__hint">
          {base.contact_person_relation2 || base.contact_person_name2
            ? `${base.contact_person_relation2 || "—"}${
                base.contact_person_name2 ? ` · ${base.contact_person_name2}` : ""
              }`
            : `Current: ${base.contact2 || "Not set"}`}
        </div>
      </div>
    </div>
  );
});

const ManageStudentContacts = () => {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadClass, setLoadClass] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setLoadClass(true);
  }, []);

  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        setDrafts([]);
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          class_id: classId,
          section_id: sectionId,
          session_id: sessionId,
        };

        let payload = null;
        try {
          const res = await axios.get(
            `${API_BASE()}getCurrentStudentForManageRecords.php`,
            { params }
          );
          payload = res?.data;
        } catch (primaryError) {
          console.warn(
            "[ManageStudentContacts] manage-records endpoint failed, falling back",
            primaryError
          );
        }

        if (!payload || payload.api_status !== "OK") {
          const res = await axios.get(`${API_BASE()}getCurrentStudent.php`, {
            params,
          });
          payload = res?.data;
        }

        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          const rows = payload.data.map((item) => {
            const contacts = normalizeContactsFromRow(item);
            return { ...item, ...contacts };
          });
          setStudents(rows);
          setDrafts(
            rows.map((item) => ({
              id: item.id,
              student_enrollment_no: item.student_enrollment_no,
              contact1: item.contact1,
              contact2: item.contact2,
              contact_id1: item.contact_id1,
              contact_id2: item.contact_id2,
              contact_person_name1: item.contact_person_name1,
              contact_person_name2: item.contact_person_name2,
              contact_person_relation1: item.contact_person_relation1,
              contact_person_relation2: item.contact_person_relation2,
              is_whatsapp1: item.is_whatsapp1,
              is_whatsapp2: item.is_whatsapp2,
            }))
          );
        } else {
          setStudents([]);
          setDrafts([]);
        }
      } catch (error) {
        console.error("[ManageStudentContacts] load error", error);
        setStudents([]);
        setDrafts([]);
        myToast.notify_warning("Failed to load students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const onClassSelect = useCallback(
    (nextClassId, nextSectionId, nextSessionId, position, className, sectionName) => {
      setSectionId(nextSectionId);
      setSessionId(nextSessionId);
      setClassSectionName(
        nextSectionId === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedClassPosition(position);
      setClassId(nextClassId);
      setSearchTerm("");
    },
    []
  );

  const handleDraftChange = useCallback((id, key, value) => {
    setDrafts((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  }, []);

  const dirtyChanges = useMemo(() => {
    const draftMap = new Map(drafts.map((d) => [d.id, d]));
    const changes = [];
    students.forEach((item) => {
      const draft = draftMap.get(item.id);
      if (!draft) return;

      const old1 = normalizeContact(item.contact1);
      const new1 = normalizeContact(draft.contact1);
      const old2 = normalizeContact(item.contact2);
      const new2 = normalizeContact(draft.contact2);
      const oldWa1 = normalizeWhatsApp(item.is_whatsapp1);
      const newWa1 = normalizeWhatsApp(draft.is_whatsapp1);
      const oldWa2 = normalizeWhatsApp(item.is_whatsapp2);
      const newWa2 = normalizeWhatsApp(draft.is_whatsapp2);

      const c1Changed = old1 !== new1;
      const c2Changed = old2 !== new2;
      const wa1Changed = oldWa1 !== newWa1;
      const wa2Changed = oldWa2 !== newWa2;
      if (!c1Changed && !c2Changed && !wa1Changed && !wa2Changed) return;

      const row = {
        student_enrollment_no: item.student_enrollment_no,
        stu_name: item.stu_name,
      };
      if (c1Changed || wa1Changed) {
        row.contact1 = old1;
        row.contact1_new = new1;
        row.contact_id1 = item.contact_id1 || "";
        row.contact_person_name1 = item.contact_person_name1 || "";
        row.contact_person_relation1 =
          item.contact_person_relation1 || "Father";
        row.is_whatsapp1 = oldWa1;
        row.is_whatsapp1_new = new1 ? newWa1 : 0;
      }
      if (c2Changed || wa2Changed) {
        row.contact2 = old2;
        row.contact2_new = new2;
        row.contact_id2 = item.contact_id2 || "";
        row.contact_person_name2 = item.contact_person_name2 || "";
        row.contact_person_relation2 =
          item.contact_person_relation2 || "Mother";
        row.is_whatsapp2 = oldWa2;
        row.is_whatsapp2_new = new2 ? newWa2 : 0;
      }
      changes.push(row);
    });
    return changes;
  }, [students, drafts]);

  const dirtyCount = dirtyChanges.length;

  const draftById = useMemo(() => {
    const map = new Map();
    drafts.forEach((d) => map.set(d.id, d));
    return map;
  }, [drafts]);

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const draft = draftById.get(s.id);
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_roll_no,
        s.contact1,
        s.contact2,
        draft?.contact1,
        draft?.contact2,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, draftById, searchTerm]);

  const submitAllChanges = useCallback(async () => {
    if (!dirtyChanges.length) {
      myToast.notify_warning("No changes to submit.");
      return;
    }

    const staffId = getStaffId();
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !schoolCode) {
      myToast.notify_warning("Staff or school code is missing.");
      return;
    }
    if (!classId || !sectionId || !sessionId) {
      myToast.notify_warning("Please select a class first.");
      return;
    }

    setSubmitting(true);
    try {
      const requestPayload = {
        staff_id: String(staffId),
        school_code: String(schoolCode),
        class_id: String(classId),
        section_id: String(sectionId),
        session_id: String(sessionId),
        student_list: JSON.stringify(dirtyChanges),
      };

      let payload = null;
      try {
        const body = new URLSearchParams(requestPayload);
        const res = await axios.post(
          `${API_BASE()}updateClassStudentContacts.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        payload = res?.data;
      } catch (postError) {
        console.warn(
          "[ManageStudentContacts] Submit All POST failed, trying GET",
          postError
        );
        const res = await axios.get(
          `${API_BASE()}updateClassStudentContacts.php`,
          { params: requestPayload }
        );
        payload = res?.data;
      }

      if (payload?.api_status === "OK") {
        myToast.notify_success(
          payload.api_remark || "Student contacts updated successfully."
        );
        await loadStudents({ soft: true });
      } else {
        myToast.notify_warning(payload?.api_remark || "Bulk update failed.");
      }
    } catch (error) {
      console.error("[ManageStudentContacts] submitAll error", error);
      myToast.notify_warning("Failed to submit changes.");
    } finally {
      setSubmitting(false);
    }
  }, [dirtyChanges, classId, sectionId, sessionId, loadStudents]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const classSelected = Boolean(classId && sectionId && sessionId);
  const busy = loading || refreshing || submitting;

  const submitButton = (
    <button
      type="button"
      className="rn-btn rn-btn--solid"
      onClick={submitAllChanges}
      disabled={busy || dirtyCount === 0}
    >
      <span className="rn-btn__icon" aria-hidden>
        <FaSave />
      </span>
      <span className="rn-btn__text">
        <span className="rn-btn__title">
          {submitting
            ? "Saving…"
            : dirtyCount > 0
            ? `Submit All (${dirtyCount})`
            : "Submit All"}
        </span>
        <span className="rn-btn__hint">
          {dirtyCount > 0 ? "Save pending edits" : "No changes yet"}
        </span>
      </span>
    </button>
  );

  return (
    <div
      className="content payroll-page sm-page rn-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Student Contacts</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaPhoneAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Student Contacts</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${students.length} student${
                      students.length === 1 ? "" : "s"
                    }`
                  : "Select a class to manage student contacts"}
                {dirtyCount > 0 ? ` · ${dirtyCount} unsaved` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="rn-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="rn-content">
          {loadClass ? (
            <div className="rn-class-wrap">
              <SelectClass
                value={loadClass}
                action={onClassSelect}
                selected_position={selectedClassPosition}
              />
            </div>
          ) : null}

          {!classSelected ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section to load students
              </p>
            </div>
          ) : loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <div className="rn-toolbar rn-toolbar--top">
                <div className="rn-toolbar__status" aria-live="polite">
                  <span className="rn-status-chip rn-status-chip--students">
                    {students.length} student
                    {students.length === 1 ? "" : "s"}
                  </span>
                  {dirtyCount > 0 ? (
                    <span className="rn-status-chip rn-status-chip--unsaved">
                      {dirtyCount} unsaved
                    </span>
                  ) : (
                    <span className="rn-status-chip rn-status-chip--clean">
                      All saved
                    </span>
                  )}
                </div>
                {submitButton}
              </div>

              {filteredStudents.length === 0 ? (
                <div className="sm-empty">
                  <p className="sm-empty__title">No students found</p>
                  <p className="sm-empty__hint">
                    {searchTerm
                      ? "Try a different search term."
                      : "This class has no active students."}
                  </p>
                </div>
              ) : (
                <div className="rn-list">
                  {filteredStudents.map((student, index) => (
                    <StudentContactRow
                      key={student.id || student.student_enrollment_no}
                      student={student}
                      draft={draftById.get(student.id)}
                      index={index}
                      onChange={handleDraftChange}
                    />
                  ))}
                </div>
              )}

              {students.length > 0 ? (
                <div className="rn-toolbar rn-toolbar--bottom">
                  {submitButton}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageStudentContacts;
